import __wbg_init_compiler, { TypstCompilerBuilder } from '@myriaddreamin/typst-ts-web-compiler';
import type { TypstCompiler } from '@myriaddreamin/typst-ts-web-compiler';
import __wbg_init_renderer, { TypstRendererBuilder } from '@myriaddreamin/typst-ts-renderer';
import type { TypstRenderer } from '@myriaddreamin/typst-ts-renderer';

// Typst files needed for label preview (.typ and their binary dependencies).
// Paths under /static/typst-labels/ are served from labels/*.typ.
// Paths under /static/typst-packages/ are served from the typst package cache.
// Both dev (out/web/static) and prod (dist/static) put these under static/.

// Paths mirror the labels/ directory layout: entry files import their lib
// via absolute project paths like `#import "/lib/30572-common.typ"`, so the
// VFS must use the same entries/ + lib/ structure rooted at /.
const LABEL_TYP_FILES = [
  'lib/common.typ',
  'lib/30572-common.typ',
  'lib/30333-common.typ',
  'entries/30572-single.typ',
  'entries/30572-dual.typ',
  'entries/30333-main.typ',
  'entries/30333-quad.typ',
];

// Binary/asset files imported by the .typ sources via `image(...)` etc.
// These are loaded into the VFS as bytes (map_shadow), not parsed as Typst.
const LABEL_ASSET_FILES = [
  'lib/30333-label-outlines.svg',
];

const TIAOMA_TYP_FILES = ['lib.typ', 'typst.toml'];
const TIAOMA_BIN_FILES = ['zint_typst_plugin.wasm'];

// Virtual path prefix the package registry reports for @preview/tiaoma:0.3.0
const TIAOMA_VIRT = '/pkg/preview/tiaoma/0.3.0';

let _compiler: TypstCompiler | null = null;
let _initPromise: Promise<TypstCompiler> | null = null;
let _renderer: TypstRenderer | null = null;
let _rendererInitPromise: Promise<TypstRenderer> | null = null;

async function fetchText(url: string): Promise<string> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
  return r.text();
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}

async function buildCompiler(): Promise<TypstCompiler> {
  await __wbg_init_compiler('/static/typst_ts_web_compiler_bg.wasm');

  const builder = new TypstCompilerBuilder();

  // Use a purely in-memory VFS — all files provided via add_source / map_shadow.
  builder.set_dummy_access_model();

  // Map @preview/tiaoma:0.3.0 to our virtual path.
  await builder.set_package_registry(
    null,
    (spec: string | { namespace: string; name: string; version: string }) => {
      let ns: string, name: string, ver: string;
      if (typeof spec === 'string') {
        const m = spec.match(/^@?([^/]+)\/([^:]+):(.+)$/);
        if (!m) return null;
        const mns = m[1]; const mname = m[2]; const mver = m[3];
        if (!mns || !mname || !mver) return null;
        ns = mns; name = mname; ver = mver;
      } else {
        ns = spec.namespace; name = spec.name; ver = spec.version;
      }
      if (ns === 'preview' && name === 'tiaoma' && ver === '0.3.0') return TIAOMA_VIRT;
      return null;
    },
  );

  // Load fonts from the manifest generated during the build.
  try {
    const resp = await fetch('/static/typst-fonts/manifest.json');
    if (resp.ok) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- fetch Response.json() returns unknown; no schema validation here
      const fontFiles = await resp.json() as string[];
      await Promise.all(fontFiles.map(async file => {
        const bytes = await fetchBytes(`/static/typst-fonts/${file}`);
        await builder.add_raw_font(bytes);
      }));
    }
  } catch (e) {
    console.warn('[typst] could not load custom fonts:', e);
  }

  const c = await builder.build();

  // Add label source files to the in-memory VFS.
  await Promise.all([
    ...LABEL_TYP_FILES.map(async file => {
      const content = await fetchText(`/static/typst-labels/${file}`);
      c.add_source(`/${file}`, content);
    }),
    ...LABEL_ASSET_FILES.map(async file => {
      const bytes = await fetchBytes(`/static/typst-labels/${file}`);
      c.map_shadow(`/${file}`, bytes);
    }),
  ]);

  // Add tiaoma package files.
  const pkgBase = '/static/typst-packages/preview/tiaoma/0.3.0';
  await Promise.all([
    ...TIAOMA_TYP_FILES.map(async file => {
      const content = await fetchText(`${pkgBase}/${file}`);
      c.add_source(`${TIAOMA_VIRT}/${file}`, content);
    }),
    ...TIAOMA_BIN_FILES.map(async file => {
      const bytes = await fetchBytes(`${pkgBase}/${file}`);
      c.map_shadow(`${TIAOMA_VIRT}/${file}`, bytes);
    }),
  ]);

  return c;
}

async function buildRenderer(): Promise<TypstRenderer> {
  await __wbg_init_renderer('/static/typst_ts_renderer_bg.wasm');
  const builder = new TypstRendererBuilder();
  return builder.build();
}

function getCompiler(): Promise<TypstCompiler> {
  if (_compiler !== null) return Promise.resolve(_compiler);
  if (_initPromise === null) {
    _initPromise = buildCompiler().then(c => { _compiler = c; return c; });
  }
  return _initPromise;
}

function getRenderer(): Promise<TypstRenderer> {
  if (_renderer !== null) return Promise.resolve(_renderer);
  if (_rendererInitPromise === null) {
    _rendererInitPromise = buildRenderer().then(r => { _renderer = r; return r; });
  }
  return _rendererInitPromise;
}

/** Begin loading the compiler and renderer in the background (call once on page load). */
export function preloadCompiler(): void {
  void getCompiler();
  void getRenderer();
}

/**
 * Compile a label file to SVG.
 * @param labelName  e.g. '30572-single'
 * @param shelcodes  shelcode values, one per slot
 */
export async function compileLabelToSvg(labelName: string, shelcodes: string[]): Promise<string> {
  const [c, renderer] = await Promise.all([getCompiler(), getRenderer()]);

  const inputs: [string, string][] = [
    ['preview', '1'],
    ...shelcodes.map((sc, i): [string, string] => [`shelcode-${i}`, sc]),
  ];

  // compile() is synchronous but called after async init — this call may block
  // the main thread briefly while typst runs.
  const result: unknown = c.compile(`/entries/${labelName}.typ`, inputs, 'vector', 0);

  // Unwrap the result — typst-ts may return Uint8Array or { result, diagnostics }.
  let vectorBytes: Uint8Array | null = null;
  if (result instanceof Uint8Array) {
    vectorBytes = result;
  } else if (result !== null && typeof result === 'object' && 'result' in result) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- typst-ts WASM result shape is not typed
    const r = result as { result?: Uint8Array | null; diagnostics?: unknown[] };
    vectorBytes = r.result ?? null;
    if (vectorBytes === null) {
      throw new Error(`Typst compilation failed:\n${JSON.stringify(r.diagnostics, null, 2)}`);
    }
  } else {
    throw new Error(`Unexpected compile result: ${String(result)}`);
  }

  const session = renderer.session_from_artifact(vectorBytes, 'vector');
  try {
    return renderer.svg_data(session);
  } finally {
    session.free();
  }
}
